import { workforceAvailability } from './workforce.js';
const formatters = new Map();
function localParts(instant, zone) {
    let f = formatters.get(zone);
    if (!f) {
        f = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        formatters.set(zone, f);
    }
    const p = Object.fromEntries(f.formatToParts(instant).map(v => [v.type, v.value]));
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}
export function workforceLocalDate(instant, zone) { return localParts(Date.parse(instant), zone).slice(0, 10); }
/** Return every matching instant. Gaps return none and folds return two: callers
 * must never silently choose a different local time or an arbitrary DST offset. */
export function workforceLocalInstants(date, minute, zone) {
    const nominal = Date.parse(date + 'T00:00:00Z') + minute * 60000, target = new Date(nominal).toISOString().slice(0, 16), offsets = new Set();
    for (let hours = -36; hours <= 36; hours += 6) {
        const t = nominal + hours * 3600000;
        offsets.add(Date.parse(localParts(t, zone) + 'Z') - t);
    }
    return [...offsets].map(offset => nominal - offset).filter(t => localParts(t, zone) === target).sort((a, b) => a - b);
}
function union(spans) {
    const result = [];
    for (const [a, b] of spans.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0])) {
        const last = result.at(-1);
        if (last && a <= last[1])
            last[1] = Math.max(last[1], b);
        else
            result.push([a, b]);
    }
    return result;
}
const minutes = (spans) => spans.reduce((sum, [a, b]) => sum + (b - a) / 60000, 0);
const clip = (spans, windows) => union(spans.flatMap(([a, b]) => windows.map(([c, d]) => [Math.max(a, c), Math.min(b, d)])));
export function projectWorkforceAvailability(row, date, source, scope) {
    const base = workforceAvailability(row, date);
    const hasStart = scope.startsAt !== undefined, hasEnd = scope.endsAt !== undefined;
    const start = Date.parse(scope.startsAt ?? ''), end = Date.parse(scope.endsAt ?? '');
    if (hasStart !== hasEnd || (hasStart && (!Number.isFinite(start) || !Number.isFinite(end) || end <= start)))
        return { ...base, status: 'Unknown', reason: 'invalid_query_interval' };
    const requirements = (source?.requirements ?? []).filter(r => r.buildingId === scope.buildingId && (!r.templateId || r.templateId === scope.templateId) && r.effectiveFrom <= date && (!r.effectiveTo || r.effectiveTo >= date));
    const eligibility = { status: requirements.some(r => r.state === 'Blocked') ? 'Blocked' : !requirements.length || requirements.some(r => r.state === 'Unknown') ? 'Unknown' : 'Recorded', requirements };
    const visibleIntervals = (source?.intervals ?? []).filter(i => {
        if (!['Active', 'Approved', 'Pending'].includes(i.status))
            return false;
        const zone = base.timeZone ?? i.timeZone, a = workforceLocalInstants(date, 0, zone), b = workforceLocalInstants(date, 1440, zone);
        return a.length === 1 && b.length === 1 && Date.parse(i.startsAt) < b[0] && Date.parse(i.endsAt) > a[0];
    });
    const conflicts = visibleIntervals.map(i => ({ id: i.id, reason: i.status === 'Pending' ? 'pending_leave' : i.kind === 'Leave' ? 'approved_leave' : i.kind === 'Training' ? 'training' : i.kind === 'Busy' ? 'busy' : 'unavailable' }));
    const result = { ...base, eligibility, conflicts, ...(source ? { availabilityVersion: source.version } : {}) };
    if (scope.startsAt && scope.endsAt && base.timeZone && (workforceLocalDate(scope.startsAt, base.timeZone) !== date || workforceLocalDate(new Date(Date.parse(scope.endsAt) - 1).toISOString(), base.timeZone) !== date))
        return { ...result, status: 'Unknown', reason: 'query_must_fit_local_day' };
    if (base.reason !== 'leave_absence_training_not_recorded')
        return result;
    if (!source)
        return result;
    if (source.workforceVersion !== row.version)
        return { ...result, status: 'Unknown', reason: 'workforce_source_changed' };
    if (date < source.reviewedFrom || date > source.reviewedTo)
        return { ...result, status: 'Unknown', reason: 'availability_not_reviewed_for_date' };
    const window = source.windows.find(w => w.patternId === base.patternId), weekday = (new Date(date + 'T12:00:00Z').getUTCDay() + 6) % 7, day = window?.days[weekday];
    if (!day || day.reduce((n, w) => n + w.end - w.start, 0) !== base.normalMinutes)
        return { ...result, status: 'Unknown', reason: 'working_windows_not_recorded' };
    const windows = [];
    for (const w of day) {
        const a = workforceLocalInstants(date, w.start, base.timeZone), b = workforceLocalInstants(date, w.end, base.timeZone);
        if (a.length !== 1 || b.length !== 1)
            return { ...result, status: 'Unknown', reason: 'working_window_clock_change_review' };
        windows.push([a[0], b[0]]);
    }
    const working = scope.startsAt && scope.endsAt ? clip(windows, [[Date.parse(scope.startsAt), Date.parse(scope.endsAt)]]) : windows;
    const applicable = source.intervals.filter(i => ['Active', 'Approved', 'Pending'].includes(i.status) && working.some(([a, b]) => Date.parse(i.startsAt) < b && Date.parse(i.endsAt) > a));
    const blocked = clip(applicable.filter(i => i.status !== 'Pending').map(i => [Date.parse(i.startsAt), Date.parse(i.endsAt)]), working);
    const pending = clip(applicable.filter(i => i.status === 'Pending').map(i => [Date.parse(i.startsAt), Date.parse(i.endsAt)]), working);
    const available = [];
    for (const [a, b] of working) {
        let cursor = a;
        for (const [c, d] of blocked) {
            if (d <= cursor || c >= b)
                continue;
            if (c > cursor)
                available.push([cursor, c]);
            cursor = Math.max(cursor, d);
        }
        if (cursor < b)
            available.push([cursor, b]);
    }
    const usableMinutes = minutes(available), workingMinutes = minutes(working);
    const requestedMinutes = hasStart ? (end - start) / 60000 : workingMinutes;
    const partial = usableMinutes < requestedMinutes;
    return { ...result, status: usableMinutes === 0 ? 'Unavailable' : partial ? 'Partial' : 'Available',
        reason: usableMinutes === 0 ? 'no_usable_working_time' : partial ? (workingMinutes < requestedMinutes ? 'outside_working_windows' : 'recorded_time_constraints') : 'reviewed_working_time',
        workingMinutes, ...(hasStart ? { requestedMinutes } : {}), usableMinutes, blockedMinutes: minutes(blocked), pendingMinutes: minutes(pending),
        availableWindows: available.map(([a, b]) => ({ startsAt: new Date(a).toISOString(), endsAt: new Date(b).toISOString() })),
        conflicts: applicable.map(i => ({ id: i.id, reason: i.status === 'Pending' ? 'pending_leave' : i.kind === 'Leave' ? 'approved_leave' : i.kind === 'Training' ? 'training' : i.kind === 'Busy' ? 'busy' : 'unavailable' })) };
}
//# sourceMappingURL=workforce-availability.js.map